<template>
  <div class="message-template-info__container">
    <div class="message-template-info__title">
      {{ messageTemplate.title }}
    </div>

    <template v-if="!disableActions">
      <div class="message-template-info__actions">
        <woot-button
          v-if="hasSendButton"
          class="message-template-info__actions__button"
          color-scheme="primary"
          icon="send"
          :is-disabled="!isSendButtonEnabled"
          data-testid="toggle-show-send-template-modal"
          @click="toggleShowSendPopup"
        >
          {{ $t('CHATLYN_MESSAGE_TEMPLATE.BUTTON.SEND_TO_LIST') }}
        </woot-button>

        <woot-button
          color-scheme="secondary"
          icon="copy"
          variant="smooth"
          data-testid="toggle-show-clone-template-modal"
          :is-disabled="isDeleting"
          @click="onCloneTemplate"
        />

        <woot-button
          color-scheme="alert"
          icon="delete"
          variant="smooth"
          data-testid="toggle-show-delete-template-modal"
          :is-disabled="isDeleting"
          @click="toggleShowDeletePopup"
        />
      </div>

      <teleport to="#chatlyn-app-target">
        <div>
          <woot-modal
            :show.sync="showSendPopup"
            :on-close="closeShowSendPopup"
            size="lg"
            overflow="scrollable"
          >
            <send-message-template-modal
              :message-template="messageTemplate"
              @close="closeShowSendPopup"
            />
          </woot-modal>

          <woot-delete-modal
            :show.sync="showDeletePopup"
            :title="$t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.TITLE')"
            :message="
              $t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.MESSAGE', {
                LANGUAGE_COUNT: messageTemplate.translations.length,
              })
            "
            :confirm-text="$t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.ACCEPT')"
            :reject-text="$t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.DECLINE')"
            :on-confirm="deleteMessageTemplate"
            :on-close="toggleShowDeletePopup"
            :is-loading="isDeleting"
            :close-on-backdrop-click="false"
            :has-buttons="!messageTemplatesEntrypoints.length"
          >
            <template v-if="messageTemplatesEntrypoints.length" #body>
              <div class="callout warning mb-4 flex flex-row gap-2 text-xs">
                <span>
                  <fluent-icon icon="lock-closed" :size="12" />
                </span>

                <span>
                  <div>
                    {{
                      $t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.ALERT.TITLE')
                    }}

                    <ul class="list-disc">
                      <li
                        v-for="entrypoint in messageTemplatesEntrypoints"
                        :key="entrypoint.entrypointId"
                        class="ml-6"
                      >
                        {{ entrypoint.name }}
                      </li>
                    </ul>

                    {{ $t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.ALERT.TEXT') }}
                  </div>
                </span>
              </div>
            </template>
          </woot-delete-modal>
        </div>
      </teleport>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';

import Teleport from 'vue2-teleport';
import SendMessageTemplateModal from './SendMessageTemplateModal.vue';
import {
  MESSAGE_TEMPLATES_CHANNEL_TYPE,
  TEMPLATE_BUILDER_MODE,
} from '../constants';

export default {
  name: 'MessageTemplateInfoActions',

  components: {
    SendMessageTemplateModal,
    Teleport,
  },

  mixins: [alertMixin],

  props: {
    messageTemplate: {
      type: Object,
      default: () => {},
    },

    disableActions: {
      type: Boolean,
      default: false,
    },

    messageTemplateType: {
      type: String,
      default: '',
    },

    isDeleting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showDeletePopup: false,
      showSendPopup: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      uiFlags: 'messageTemplates/getUIFlags',
      messageTemplatesEntrypoints:
        'messageTemplates/getMessageTemplatesEntrypoints',
    }),

    isSendButtonEnabled() {
      return (
        (this.messageTemplate?.translations?.some(
          (translation) => translation.status === 'approved'
        ) ||
          this.messageTemplate.status === 'approved') &&
        !this.isConsent &&
        !this.isDeleting
      );
    },

    isConsent() {
      return this.messageTemplate?.category === 'CONSENT';
    },

    hasSendButton() {
      return this.messageTemplateType !== MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS;
    },
  },

  watch: {
    'messageTemplate._id': {
      async handler(newValue) {
        if (!newValue) {
          return;
        }

        await this.fetchMessageTemplateEntrypoints();
      },
      immediate: true,
    },
  },

  methods: {
    toggleShowDeletePopup() {
      this.showDeletePopup = !this.showDeletePopup;
    },

    toggleShowSendPopup() {
      if (this.isConsent) {
        return;
      }

      this.showSendPopup = !this.showSendPopup;
    },
    onCloneTemplate() {
      this.$emit('clone-template', TEMPLATE_BUILDER_MODE.DUPLICATE);
    },

    closeShowSendPopup() {
      this.showSendPopup = false;
    },

    fetchMessageTemplates() {
      this.$emit('fetch-message-templates');
    },

    fetchMessageTemplate(id) {
      this.$emit('fetch-message-template', id);
    },

    async deleteMessageTemplate() {
      this.$emit('delete-template');
      this.toggleShowDeletePopup();
    },

    async fetchMessageTemplateEntrypoints() {
      const result = await this.$store.dispatch(
        'messageTemplates/getMessageTemplatesEntrypoints',
        {
          accountId: this.accountId,
          // eslint-disable-next-line no-underscore-dangle
          templateId: this.messageTemplate._id,
          templateName: this.messageTemplate.name || this.messageTemplate.title,
        }
      );

      if (!result) {
        this.showAlert(
          this.$t('CHATLYN_MESSAGE_TEMPLATE.PANEL.ENTRYPOINTS_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-template-info__container {
  @apply flex flex-col flex-nowrap gap-[0.625rem];

  .message-template-info__title {
    @apply overflow-hidden text-ellipsis text-lg leading-[1.39] text-woot-900 dark:text-woot-50;
  }

  .message-template-info__actions {
    @apply flex flex-row flex-nowrap items-center justify-start gap-[0.625rem];

    .message-template-info__actions__button {
      @apply flex flex-1 items-center justify-center;

      ::v-deep .button__content {
        @apply w-auto;
      }
    }
  }
}
</style>
